// ABOUTME: Orchestration hook for the Order Cancellation Lead Time entity — combines config query, mutations, and form state.
// ABOUTME: Returns a uniform interface consumed by CountryConfigurationsEntity to keep the page component thin.
import { useCallback, useMemo, useRef } from 'react'
import { useOrderCancellationLeadTimeConfig } from './useOrderCancellationLeadTimeConfig'
import { useCreateOrderCancellationLeadTime, useCreateOrderCancellationLeadTimeWithScope, useUpdateOrderCancellationLeadTime, useDeleteOrderCancellationLeadTime } from './useOrderCancellationLeadTimeMutations'
import { useBusinessDays } from './useBusinessDays'
import { OrderCancellationLeadTimeConfig, OrderCancellationLeadTimeFormValues, OrderCancellationLeadTimeOverrides } from '../types/orderCancellationLeadTime'
import { DayIdentifier } from '../types/deliveryWindow'
import { EntityActionResult } from '../context/EntityFormContext'
import type { PendingScopeRequest } from '../context/EntityFormContext'
import { getConfigScopeId, toPendingScopeConfig } from '../utils/pendingScope'

interface UseOrderCancellationLeadTimeEntityParams {
  countryId: string | undefined
  businessLine: string | undefined
  scopeId: string | undefined
  pendingScope?: PendingScopeRequest | undefined
  rootScopeId?: string | undefined
}

interface EntityActions {
  handleSave: (onSuccess: (result?: EntityActionResult) => void, onError?: (error?: unknown) => void) => void
  handleReset: (onSuccess: (result?: EntityActionResult) => void, onError?: (error?: unknown) => void) => void
  handleValuesChange: (values: OrderCancellationLeadTimeFormValues) => void
}

interface UseOrderCancellationLeadTimeEntityResult {
  config: OrderCancellationLeadTimeConfig | undefined
  isLoading: boolean
  error: Error | null
  isSaving: boolean
  actions: EntityActions
  nonBusinessDays: DayIdentifier[]
}

const equalsParent = (values: OrderCancellationLeadTimeFormValues, config: OrderCancellationLeadTimeConfig): boolean =>
  JSON.stringify(values) === JSON.stringify(config.parentValues)

export const useOrderCancellationLeadTimeEntity = ({ countryId, businessLine, scopeId, pendingScope, rootScopeId }: UseOrderCancellationLeadTimeEntityParams): UseOrderCancellationLeadTimeEntityResult => {
  const configScopeId = getConfigScopeId(scopeId, pendingScope, rootScopeId)
  const { data: rawConfig, isLoading: isConfigLoading, error } = useOrderCancellationLeadTimeConfig(countryId, businessLine, configScopeId, rootScopeId)
  const config = useMemo(() => toPendingScopeConfig(rawConfig, pendingScope, (cfg) => ({
    farDistanceThreshold: cfg.farDistanceThreshold,
    farDistanceThresholdUomId: cfg.farDistanceThresholdUomId ?? '',
    farDistanceThresholdUomDisplay: cfg.farDistanceThresholdUomDisplay,
    standard: cfg.standard,
    nonStandard: cfg.nonStandard,
    distanceUomOptions: cfg.distanceUomOptions,
  })), [rawConfig, pendingScope])
  const { nonBusinessDays, isLoading: isBusinessDaysLoading } = useBusinessDays(countryId, businessLine, configScopeId, rootScopeId)
  const createMutation = useCreateOrderCancellationLeadTime(countryId, businessLine)
  const createWithScopeMutation = useCreateOrderCancellationLeadTimeWithScope(countryId, businessLine)
  const updateMutation = useUpdateOrderCancellationLeadTime(countryId, businessLine)
  const deleteMutation = useDeleteOrderCancellationLeadTime(countryId, businessLine)
  const formValuesRef = useRef<OrderCancellationLeadTimeFormValues | null>(null)
  const configIdRef = useRef<string | undefined>(config?.id)
  const mutationsRef = useRef({ createMutation, createWithScopeMutation, updateMutation, deleteMutation })
  const configRef = useRef<OrderCancellationLeadTimeConfig | undefined>(config)
  const pendingScopeRef = useRef<PendingScopeRequest | undefined>(pendingScope)

  configIdRef.current = config?.id
  configRef.current = config
  pendingScopeRef.current = pendingScope
  mutationsRef.current = { createMutation, createWithScopeMutation, updateMutation, deleteMutation }

  const handleValuesChange = useCallback((values: OrderCancellationLeadTimeFormValues) => { formValuesRef.current = values }, [])
  const handleSave = useCallback(
    (onSuccess: (result?: EntityActionResult) => void, onError?: (error?: unknown) => void) => {
      if (!formValuesRef.current || !formValuesRef.current.standard || !configRef.current) return
      const values = formValuesRef.current
      const scopeToCreate = pendingScopeRef.current

      if (scopeToCreate) {
        if (equalsParent(values, configRef.current)) {
          onSuccess({})
          return
        }
        const overrides: OrderCancellationLeadTimeOverrides = { farDistanceThreshold: true, leadTimes: true }
        mutationsRef.current.createWithScopeMutation.mutate({ scope: scopeToCreate, values, overrides }, { onSuccess: (result) => onSuccess({ wasOverriding: true, createdScopeId: result?.scopeId }), onError: (error) => onError?.(error) })
        return
      }

      if (!scopeId) return

      if (scopeId !== rootScopeId && equalsParent(values, configRef.current)) {
        if (configIdRef.current) {
          mutationsRef.current.deleteMutation.mutate(scopeId, { onSuccess: () => onSuccess({ wasDelete: true }), onError: (error) => onError?.(error) })
        } else {
          onSuccess({ wasDelete: true })
        }
        return
      }

      const overrides: OrderCancellationLeadTimeOverrides = { farDistanceThreshold: true, leadTimes: true }
      const isNew = !configIdRef.current
      const mutate = isNew ? mutationsRef.current.createMutation.mutate : mutationsRef.current.updateMutation.mutate
      mutate({ scopeId, values, overrides }, { onSuccess: () => onSuccess({ wasOverriding: true }), onError: (error) => onError?.(error) })
    },
    [scopeId, rootScopeId]
  )
  const handleReset = useCallback(
    (onSuccess: (result?: EntityActionResult) => void, onError?: (error?: unknown) => void) => {
      if (!scopeId) return
      mutationsRef.current.deleteMutation.mutate(scopeId, { onSuccess: () => onSuccess({ wasDelete: true }), onError: (error) => onError?.(error) })
    },
    [scopeId]
  )
  const actions = useMemo(() => ({ handleSave, handleReset, handleValuesChange }), [handleSave, handleReset, handleValuesChange])
  const isSaving = createMutation.isPending || createWithScopeMutation.isPending || updateMutation.isPending || deleteMutation.isPending
  const isLoading = isConfigLoading || isBusinessDaysLoading
  return { config, isLoading, error, isSaving, actions, nonBusinessDays }
}
